import React from 'react';
import { ArrowLeft, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

const PrivacyView = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // Engelska per default, endast svenska om språket uttryckligen är satt till 'sv'
  const isSv = language === 'sv';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 p-6 md:p-12">
      <div className="max-w-3xl mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-8">
        
        {/* Tillbaka-knapp */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 dark:hover:text-white mb-6 transition-colors cursor-pointer"
        >
          <ArrowLeft size={16} />
          {t(language, 'common_back') || (isSv ? 'Tillbaka' : 'Back')}
        </button>

        {/* Rubrik */}
        <div className="flex items-center gap-3 mb-6">
          <Lock className="text-indigo-600 dark:text-indigo-400" size={32} />
          <h1 className="text-2xl md:text-3xl font-bold">
            {isSv ? 'Integritetspolicy' : 'Privacy Policy'}
          </h1>
        </div>

        <p className="text-sm text-slate-500 mb-6">
          {isSv ? 'Senast uppdaterad: Oktober 2026' : 'Last updated: October 2026'}
        </p>

        {/* Innehåll */}
        <div className="space-y-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          
          {/* Sektion 1 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '1. Personuppgifter vi behandlar' : '1. Personal Data We Process'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Vi behandlar din e-postadress och ditt användar-ID från Supabase Auth vid inloggning med Google eller Apple OAuth. Om du väljer <em>"Dölj min e-postadress"</em> via Apple Sign In tar vi emot en anonymiserad reläadress (som slutar på <code>@privaterelay.appleid.com</code>), vilken uteslutande fungerar som ett tekniskt konto-ID. Vid betalning behandlar Stripe dina kort- och faktureringsuppgifter; inga fullständiga kortnummer sparas på våra servrar.
                </>
              ) : (
                <>
                  We process your email address and unique user ID from Supabase Auth when you sign in via Google or Apple OAuth. If you choose <em>"Hide My Email"</em> via Apple Sign In, we receive an anonymized private relay address (ending in <code>@privaterelay.appleid.com</code>), which serves strictly as a technical account identifier. When subscribing, Stripe processes your payment details securely; no raw card numbers are ever processed or stored on our servers.
                </>
              )}
            </p>
          </section>

          {/* Sektion 2 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '2. Ändamål och laglig grund' : '2. Purpose & Legal Basis'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Dina uppgifter används med fullgörande av avtal (GDPR Art. 6.1b) som laglig grund för att autentisera dig, leverera sparade bevakningar och inkorgsträffar, skicka dagliga forskningsnotiser via e-post, samt administrera ditt prenumerationsavtal. Vi säljer eller delar aldrig dina uppgifter med tredjepartsannonsörer.
                </>
              ) : (
                <>
                  Your information is processed under the legal basis of contract performance (GDPR Art. 6.1b) solely to authenticate your account, maintain your research watches, generate AI summaries in your inbox, deliver daily morning digests via email, and manage your subscription. We never sell, rent, or share personal data with third-party advertisers.
                </>
              )}
            </p>
          </section>

          {/* Sektion 3 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '3. Tredjepartsleverantörer (Underbiträden)' : '3. Third-Party Service Providers (Data Processors)'}
            </h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>
                <strong>Supabase</strong>: {isSv ? 'Databaslagring och inloggning (EU/EES).' : 'Database hosting and authentication (EU/EEA).'}
              </li>
              <li>
                <strong>Stripe</strong>: {isSv ? 'Säker betalningsinfrastruktur.' : 'Encrypted payment processing.'}
              </li>
              <li>
                <strong>Mistral AI</strong>: {isSv ? 'Europeisk AI-infrastruktur för sammanfattning av abstrakt. Inga personuppgifter skickas till AI-modellen.' : 'European AI infrastructure for scientific abstract synthesis. No personal user data is ever sent to Mistral.'}
              </li>
              <li>
                <strong>Resend</strong>: {isSv ? 'Teknisk distribution av dagliga forskningsnotiser och transaktionsmejl.' : 'Delivery of daily research briefings and notification emails.'}
              </li>
            </ul>
          </section>

          {/* Sektion 4 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '4. Datalagring och automatisk rensning' : '4. Data Retention & Cleanup'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Olästa artiklar i din inkorg rensas automatiskt bort efter 30 dagar för att minimera datalagring och bibehålla god prestanda. Sparade artiklar sparas tills du själv väljer att radera dem.
                </>
              ) : (
                <>
                  Unread articles in your inbox are automatically pruned after 30 days to ensure data minimization and keep the service performant. Saved articles remain in your archive until you choose to delete them.
                </>
              )}
            </p>
          </section>

          {/* Sektion 5 */}
          <section>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              {isSv ? '5. Dina rättigheter enligt GDPR' : '5. Your Rights Under GDPR'}
            </h2>
            <p>
              {isSv ? (
                <>
                  Du har rätt att begära ett utdrag av dina personuppgifter, begära rättelse samt begära att ditt konto och all tillhörande data raderas permanent (”rätten att bli bortglömd”). Kontakta oss på <a href="mailto:fredrik.mokvist@gmail.com" className="text-indigo-600 dark:text-indigo-400 underline">fredrik.mokvist@gmail.com</a> för att utöva dina rättigheter.
                </>
              ) : (
                <>
                  You have the right to request an extract of all data we hold about you, request corrections, or request complete account and data deletion ("the right to be forgotten"). To exercise these rights or pause automated email notifications, manage your settings in the app or contact us at <a href="mailto:fredrik.mokvist@gmail.com" className="text-indigo-600 dark:text-indigo-400 underline">fredrik.mokvist@gmail.com</a>.
                </>
              )}
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyView;